/**
 * Avatar emoji set and their fixed background colors.
 *
 * The background color is a pure lookup from the emoji, so every place that
 * renders an avatar (your own UI, friends list, leaderboards, co-op) resolves
 * the same color with no settings field or network payload — anyone who sees
 * the emoji sees the color.
 */

export const AVATARS = [
	'🐱', '🐶', '🦊', '🐸', '🐼', '🦁', '🐯', '🐨',
	'🐻', '🦄', '🐺', '🦝', '🐷', '🐰', '🐮', '🐧',
	'🐑', '🦋', '🐢', '🦖', '🐙', '🦀', '🐝', '🦉', '🦩',
	'🧙', '🧝', '🧛', '🤖', '👾', '🎮', '🌟', '🔥'
] as const;

const AVATAR_BG: Record<string, string> = {
	'🐱': '#F4C7AB',
	'🐶': '#E8C39E',
	'🦊': '#F2A65A',
	'🐸': '#A8D08D',
	'🐼': '#D3D9E0',
	'🦁': '#F2C14E',
	'🐯': '#F0A868',
	'🐨': '#C9CBD3',
	'🐻': '#D9B38C',
	'🦄': '#E9C7E8',
	'🐺': '#B9C2CE',
	'🦝': '#C7CDD6',
	'🐷': '#F4C2D0',
	'🐰': '#EBD3DA',
	'🐮': '#E5D9C6',
	'🐧': '#BFD0DE',
	'🐑': '#E3E0D6',
	'🦋': '#B7D7E8',
	'🐢': '#A9CFA3',
	'🦖': '#9FCFA0',
	'🐙': '#E7A9B7',
	'🦀': '#EFA38C',
	'🐝': '#F2D06B',
	'🦉': '#CDBFA0',
	'🦩': '#F4B8C8',
	'🧙': '#BBA8E0',
	'🧝': '#AED9C0',
	'🧛': '#C9B6D6',
	'🤖': '#BCC8D6',
	'👾': '#B7C7E8',
	'🎮': '#C2BEE8',
	'🌟': '#F3D873',
	'🔥': '#F2A66B'
};

const DEFAULT_BG = '#C9CDD6';

export const AVATAR_INITIALS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('') as readonly string[];

// Reuse the exact emoji tint colors, cycled across A–Z.
const INITIAL_COLORS = [...new Set(Object.values(AVATAR_BG))];

/** True when the avatar is a single A–Z initial (vs an emoji or empty). */
export function isInitialAvatar(avatar: string): boolean {
	return avatar.length === 1 && avatar >= 'A' && avatar <= 'Z';
}

// DiceBear avatars. Stored compactly as `<prefix><seed>` (e.g. `db:Easton`) so
// the value stays well under the 16-char leaderboard avatar cap; the SVG URL
// is rebuilt at render time. Each style has its own short prefix. Keep seeds
// short. Note no prefix is a prefix of another, so match order is irrelevant.
export type DicebearStyle = { id: string; prefix: string; label: string; params?: string };

export const DICEBEAR_STYLES: DicebearStyle[] = [
	{ id: 'fun-emoji', prefix: 'dbf:', label: 'Fun Emoji' },
	{ id: 'adventurer-neutral', prefix: 'db:', label: 'Characters' },
	{ id: 'bottts-neutral', prefix: 'dbb:', label: 'Robots' },
	{ id: 'croodles', prefix: 'dbc:', label: 'Doodles', params: 'backgroundColor=b6e3f4,c0aede,d1d4f9,ffd5dc,ffdfbf' },
	{ id: 'pixel-art', prefix: 'dbp:', label: 'Pixel Art', params: 'backgroundColor=b6e3f4,c0aede,d1d4f9,ffdfbf,ffd5dc&hairColor=000000,28150a,603a14,a55728,b58143,d6b370&accessoriesProbability=0&glassesProbability=0&hatProbability=0&beardProbability=0' },
	{ id: 'thumbs', prefix: 'dbt:', label: 'Thumbs', params: 'backgroundColor=0a5b83,1c799f,69d2e7,f1f4dc,f88c49,b6e3f4,c0aede,d1d4f9,ffd5dc,ffdfbf' },
	{ id: 'lorelei', prefix: 'dbl:', label: 'Lorelei', params: 'backgroundColor=b6e3f4,c0aede,d1d4f9,ffd5dc,ffdfbf' },
	{ id: 'notionists-neutral', prefix: 'dbn:', label: 'Notionists', params: 'backgroundColor=ffffff,ffdfbf,ffd5dc,d1d4f9,c0aede,b6e3f4' }
];

export const DICEBEAR_SEEDS = [
	'Easton', 'Felix', 'Luna', 'Milo', 'Zoe', 'Leo', 'Maya', 'Finn',
	'Ivy', 'Kai', 'Nova', 'Aria', 'Jack', 'Ruby', 'Oscar', 'Lily',
	'Sam', 'Mia', 'Eden', 'Remy', 'Cleo', 'Theo', 'Wren', 'Juno'
] as const;

function dicebearStyleFor(avatar: string): DicebearStyle | undefined {
	return DICEBEAR_STYLES.find((s) => avatar.startsWith(s.prefix));
}

export function dicebearToken(style: DicebearStyle, seed: string): string {
	return style.prefix + seed;
}

export function isDicebearAvatar(avatar: string): boolean {
	return dicebearStyleFor(avatar) !== undefined;
}

export function dicebearUrl(avatar: string): string {
	const style = dicebearStyleFor(avatar);
	if (!style) return '';
	const seed = avatar.slice(style.prefix.length);
	// Served from our own origin (pre-generated by scripts/fetch-dicebear-avatars.mjs)
	// — no runtime request to api.dicebear.com, so it's reliable and offline-capable.
	return `/avatars/${style.id}/${encodeURIComponent(seed)}.svg`;
}

function hashSeed(s: string): number {
	let h = 0;
	for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
	return h;
}

/** The glyph actually shown: the avatar, or the username's first letter. */
export function avatarGlyph(avatar: string, username: string): string {
	return avatar || (username || '?').charAt(0).toUpperCase();
}

/** True when the shown glyph is a letter (chosen initial or username
 * fallback) and so wants the dark ink + bold treatment, not an emoji. */
export function avatarIsLetter(avatar: string, username: string): boolean {
	if (isDicebearAvatar(avatar)) return false;
	return avatar ? isInitialAvatar(avatar) : true;
}

/** Background tint for whatever glyph is shown — always a color, including
 * the username-initial fallback. */
export function avatarTint(avatar: string, username: string): string {
	if (isDicebearAvatar(avatar)) return INITIAL_COLORS[hashSeed(avatar) % INITIAL_COLORS.length];
	return avatarBg(avatarGlyph(avatar, username)) ?? DEFAULT_BG;
}

/**
 * Background color for an avatar (emoji or a single A–Z initial). Returns null
 * for the empty avatar (no avatar → fall back to the default initial frame).
 */
export function avatarBg(avatar: string): string | null {
	if (!avatar) return null;
	if (avatar in AVATAR_BG) return AVATAR_BG[avatar];
	if (avatar.length === 1 && avatar >= 'A' && avatar <= 'Z') {
		return INITIAL_COLORS[(avatar.charCodeAt(0) - 65) % INITIAL_COLORS.length];
	}
	return DEFAULT_BG;
}
